import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined"
import AddIcon from "@mui/icons-material/Add"
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined"
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined"
import RefreshIcon from "@mui/icons-material/Refresh"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import * as hrPayrollService from "../../services/hrPayrollService"


// =========================================================
// HELPERS
// =========================================================

const money = (
  value,
  currency = "USD"
) =>
  `${new Intl.NumberFormat(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  ).format(
    Number(value || 0)
  )} ${currency || "USD"}`


const firstDayOfMonth = () => {
  const now = new Date()

  return [
    now.getFullYear(),
    String(
      now.getMonth() + 1
    ).padStart(2, "0"),
    "01",
  ].join("-")
}


const lastDayOfMonth = () => {
  const now = new Date()

  const last = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0
  )

  return [
    last.getFullYear(),
    String(
      last.getMonth() + 1
    ).padStart(2, "0"),
    String(
      last.getDate()
    ).padStart(2, "0"),
  ].join("-")
}


const currentMonthName = () =>
  new Intl.DateTimeFormat(
    "fr-FR",
    {
      month: "long",
      year: "numeric",
    }
  ).format(
    new Date()
  )


const formatDate = (
  value
) => {
  if (!value) {
    return "-"
  }

  try {
    const date = new Date(
      `${String(value).slice(
        0,
        10
      )}T00:00:00`
    )

    return new Intl.DateTimeFormat(
      "fr-FR"
    ).format(
      date
    )
  } catch {
    return value
  }
}


const normalizeItems = (
  result
) => {
  if (
    Array.isArray(
      result
    )
  ) {
    return result
  }

  return (
    result?.items ||
    result?.data ||
    []
  )
}


const extractError = (
  err,
  fallback
) => {
  const detail =
    err?.response?.data
      ?.detail

  if (
    typeof detail ===
    "string"
  ) {
    return detail
  }

  if (
    detail?.message
  ) {
    return detail.message
  }

  if (
    detail?.database_error
  ) {
    return detail.database_error
  }

  return (
    err?.message ||
    fallback
  )
}


const statusConfig = {
  DRAFT: {
    label:
      "Brouillon",
    color:
      "default",
  },

  VALIDATED: {
    label:
      "Validé",
    color:
      "info",
  },

  PAID: {
    label:
      "Payé",
    color:
      "success",
  },

  CANCELLED: {
    label:
      "Annulé",
    color:
      "error",
  },

  CLOSED: {
    label:
      "Clôturée",
    color:
      "default",
  },
}


const resolveStatus = (
  value
) => {
  const key =
    String(
      value || ""
    ).toUpperCase()

  return (
    statusConfig[
      key
    ] || {
      label:
        value || "-",
      color:
        "default",
    }
  )
}


// =========================================================
// KPI CARD
// =========================================================

function KpiCard({
  title,
  value,
  subtitle,
  icon,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: {
          xs: 1.6,
          sm: 2,
        },

        minHeight: 116,

        border:
          "1px solid",

        borderColor:
          "divider",

        borderRadius: 3,

        bgcolor:
          "background.paper",
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            borderRadius: 2,

            flexShrink: 0,

            display:
              "grid",

            placeItems:
              "center",

            bgcolor:
              "action.hover",
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {title}
          </Typography>

          <Typography
            fontWeight={900}
            sx={{
              fontSize: {
                xs: "1.4rem",
                md: "1.55rem",
              },

              lineHeight: 1.2,

              mt: 0.25,

              overflowWrap:
                "anywhere",
            }}
          >
            {value}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}


// =========================================================
// FORM SECTION
// =========================================================

function FormSection({
  title,
  subtitle,
  children,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        border:
          "1px solid",

        borderColor:
          "divider",

        borderRadius: 2.5,

        overflow:
          "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.4,

          bgcolor:
            "action.hover",
        }}
      >
        <Typography
          fontWeight={800}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          p: 2,
        }}
      >
        {children}
      </Box>
    </Paper>
  )
}


// =========================================================
// NUMBER FIELD
// =========================================================

function NumberField({
  label,
  value,
  onChange,
  helperText = "",
  disabled = false,
}) {
  return (
    <TextField
      type="number"
      label={label}
      value={value}
      onChange={onChange}
      helperText={helperText}
      disabled={disabled}
      fullWidth
      slotProps={{
        htmlInput: {
          min: 0,
          step: "0.01",
        },
      }}
    />
  )
}


// =========================================================
// MOBILE PAYROLL CARD
// =========================================================

function MobilePayrollCard({
  row,
  onValidate,
  onPay,
  onDelete,
  onDownloadPdf,
}) {
  const status =
    String(
      row.status || ""
    ).toUpperCase()

  const config =
    resolveStatus(
      status
    )

  const additions =
    Number(
      row.bonuses || 0
    )
    +
    Number(
      row.commissions || 0
    )
    +
    Number(
      row.overtime_amount || 0
    )
    +
    Number(
      row.allowances || 0
    )

  const reductions =
    Number(
      row.deductions || 0
    )
    +
    Number(
      row.advances || 0
    )

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.7,

        border:
          "1px solid",

        borderColor:
          "divider",

        borderRadius: 3,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography
            fontWeight={900}
            noWrap
          >
            {row.employee_name ||
              "Employé"}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            {row.employee_code ||
              "-"}
            {" • "}
            {row.period_name ||
              row.period_code ||
              "-"}
          </Typography>
        </Box>

        <Chip
          size="small"
          label={config.label}
          color={config.color}
        />
      </Stack>

      <Divider
        sx={{
          my: 1.5,
        }}
      />

      <Box
        sx={{
          display:
            "grid",

          gridTemplateColumns:
            "1fr 1fr",

          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            Salaire de base
          </Typography>

          <Typography
            fontWeight={700}
          >
            {money(
              row.base_salary,
              row.currency
            )}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            Éléments +
          </Typography>

          <Typography
            fontWeight={700}
          >
            {money(
              additions,
              row.currency
            )}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            Retenues
          </Typography>

          <Typography
            fontWeight={700}
          >
            {money(
              reductions,
              row.currency
            )}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            Brut
          </Typography>

          <Typography
            fontWeight={700}
          >
            {money(
              row.gross_salary,
              row.currency
            )}
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mt: 1.5,

          px: 1.5,
          py: 1.2,

          bgcolor:
            "action.hover",

          borderRadius: 2,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
        >
          Net à payer
        </Typography>

        <Typography
          variant="h6"
          fontWeight={900}
        >
          {money(
            row.net_salary ??
              row.net_amount,
            row.currency
          )}
        </Typography>
      </Paper>

      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{
          mt: 1.5,
        }}
      >
        {status ===
          "DRAFT" && (
          <Button
            size="small"
            variant="contained"
            onClick={() =>
              onValidate(
                row.id
              )
            }
          >
            Valider
          </Button>
        )}

        {status ===
          "VALIDATED" && (
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() =>
              onPay(
                row.id
              )
            }
          >
            Enregistrer paiement
          </Button>
        )}

        {status !==
          "PAID" && (
          <Button
            size="small"
            color="error"
            onClick={() =>
              onDelete(
                row
              )
            }
          >
            Supprimer
          </Button>
        )}

        <Button
          size="small"
          variant="outlined"
          startIcon={
            <DownloadOutlinedIcon />
          }
          onClick={() =>
            onDownloadPdf(
              row
            )
          }
        >
          PDF
        </Button>

        {status ===
          "PAID" && (
          <Typography
            variant="caption"
            color="success.main"
            fontWeight={800}
          >
            Paiement terminé
          </Typography>
        )}
      </Stack>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

export default function PayrollPage() {
  const theme =
    useTheme()

  const mobile =
    useMediaQuery(
      theme.breakpoints.down(
        "md"
      )
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    rows,
    setRows,
  ] = useState([])

  const [
    periods,
    setPeriods,
  ] = useState([])

  const [
    employees,
    setEmployees,
  ] = useState([])

  const [
    summary,
    setSummary,
  ] = useState({})


  // =======================================================
  // PAGE STATE
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    downloadingPdfId,
    setDownloadingPdfId,
  ] = useState(null)

  const [
    error,
    setError,
  ] = useState("")


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    selectedPeriod,
    setSelectedPeriod,
  ] = useState("all")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all")

  const [
    search,
    setSearch,
  ] = useState("")


  // =======================================================
  // DIALOGS
  // =======================================================

  const [
    periodDialogOpen,
    setPeriodDialogOpen,
  ] = useState(false)

  const [
    periodsManagerOpen,
    setPeriodsManagerOpen,
  ] = useState(false)

  const [
    payrollDialogOpen,
    setPayrollDialogOpen,
  ] = useState(false)


  // =======================================================
  // PERIOD FORM
  // =======================================================

  const initialPeriodForm =
    () => ({
      name:
        currentMonthName(),

      period_start:
        firstDayOfMonth(),

      period_end:
        lastDayOfMonth(),

      payment_date:
        "",
    })


  const [
    periodForm,
    setPeriodForm,
  ] = useState(
    initialPeriodForm
  )


  // =======================================================
  // PAYROLL FORM
  // =======================================================

  const initialPayrollForm =
    () => ({
      payroll_period_id:
        "",

      employee_id:
        "",

      base_salary:
        0,

      bonuses:
        0,

      commissions:
        0,

      overtime_amount:
        0,

      use_automatic_overtime:
        true,

      allowances:
        0,

      deductions:
        0,

      advances:
        0,

      currency:
        "USD",

      notes:
        "",
    })


  const [
    payrollForm,
    setPayrollForm,
  ] = useState(
    initialPayrollForm
  )


  const [
    payrollPreview,
    setPayrollPreview,
  ] = useState(null)


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open:
      false,

    severity:
      "success",

    message:
      "",
  })


  const showMessage = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open:
        true,

      severity,

      message,
    })
  }


  // =======================================================
  // LOAD FORM DATA
  // =======================================================

  const loadFormData =
    useCallback(
      async () => {
        try {
          const result =
            await hrPayrollService
              .getHrPayrollFormData()

          setEmployees(
            result?.employees ||
              []
          )

          setPeriods(
            result?.periods ||
              []
          )
        } catch (err) {
          console.error(
            "PAYROLL FORM DATA ERROR:",
            err
          )
        }
      },
      []
    )


  // =======================================================
  // LOAD DATA
  // =======================================================

  const loadData =
    useCallback(
      async () => {
        setLoading(true)
        setError("")

        try {
          const params = {
            payroll_period_id:
              selectedPeriod,

            status:
              statusFilter,

            search:
              search.trim(),
          }

          const payrollResult =
            await hrPayrollService
              .getHrPayrolls(
                params
              )

          let summaryResult =
            {}

          try {
            summaryResult =
              await hrPayrollService
                .getHrPayrollSummary(
                  {
                    payroll_period_id:
                      selectedPeriod,

                    status:
                      statusFilter,
                  }
                )
          } catch (
            summaryError
          ) {
            console.error(
              "PAYROLL SUMMARY ERROR:",
              summaryError
            )
          }

          setRows(
            normalizeItems(
              payrollResult
            )
          )

          setSummary(
            summaryResult ||
              {}
          )
        } catch (err) {
          setError(
            extractError(
              err,
              "Impossible de charger les données de paie."
            )
          )
        } finally {
          setLoading(false)
        }
      },
      [
        selectedPeriod,
        statusFilter,
        search,
      ]
    )


  // =======================================================
  // EFFECTS
  // =======================================================

  useEffect(
    () => {
      loadFormData()
    },
    [
      loadFormData,
    ]
  )


  useEffect(
    () => {
      loadData()
    },
    [
      loadData,
    ]
  )


  // =======================================================
  // TOTALS
  // =======================================================

  const totals =
    useMemo(
      () => {
        const rowGross =
          rows.reduce(
            (
              total,
              row
            ) =>
              total +
              Number(
                row.gross_salary ||
                  0
              ),
            0
          )

        const rowNet =
          rows.reduce(
            (
              total,
              row
            ) =>
              total +
              Number(
                row.net_salary ??
                  row.net_amount ??
                  0
              ),
            0
          )

        const rowDeductions =
          rows.reduce(
            (
              total,
              row
            ) =>
              total +
              Number(
                row.deductions ||
                  0
              )
              +
              Number(
                row.advances ||
                  0
              ),
            0
          )

        const rowPaid =
          rows.filter(
            (row) =>
              String(
                row.status ||
                  ""
              ).toUpperCase()
              === "PAID"
          ).length

        const deductions =
          Number(
            summary.deductions_total ??
              0
          )
          +
          Number(
            summary.advances_total ??
              0
          )

        return {
          count:
            Number(
              summary.payroll_count ??
                rows.length
            ),

          gross:
            Number(
              summary.gross_total ??
                rowGross
            ),

          net:
            Number(
              summary.net_total ??
                rowNet
            ),

          deductions:
            deductions ||
            rowDeductions,

          paid:
            Number(
              summary.paid_count ??
                rowPaid
            ),
        }
      },
      [
        summary,
        rows,
      ]
    )


  // =======================================================
  // FORM CALCULATION
  // =======================================================

  const formCalculation =
    useMemo(
      () => {
        const gross =
          Number(
            payrollForm.base_salary ||
              0
          )
          +
          Number(
            payrollForm.bonuses ||
              0
          )
          +
          Number(
            payrollForm.commissions ||
              0
          )
          +
          Number(
            payrollForm.overtime_amount ||
              0
          )
          +
          Number(
            payrollForm.allowances ||
              0
          )

        const reductions =
          Number(
            payrollForm.deductions ||
              0
          )
          +
          Number(
            payrollForm.advances ||
              0
          )

        return {
          gross,
          reductions,

          net:
            Math.max(
              gross -
                reductions,
              0
            ),
        }
      },
      [
        payrollForm,
      ]
    )


  // =======================================================
  // REFRESH
  // =======================================================

  const refreshAll =
    async () => {
      await Promise.all([
        loadFormData(),
        loadData(),
      ])
    }


  // =======================================================
  // CREATE PERIOD
  // =======================================================

  const createPeriod =
    async () => {
      if (
        !periodForm.name
          ?.trim() ||
        !periodForm
          .period_start ||
        !periodForm
          .period_end
      ) {
        showMessage(
          "Complétez le nom et les dates de la période.",
          "warning"
        )

        return
      }

      if (
        periodForm.period_end <
        periodForm.period_start
      ) {
        showMessage(
          "La date de fin ne peut pas être antérieure à la date de début.",
          "warning"
        )

        return
      }

      setSaving(true)

      try {
        const result =
          await hrPayrollService
            .createHrPayrollPeriod(
              {
                name:
                  periodForm.name
                    .trim(),

                period_start:
                  periodForm
                    .period_start,

                period_end:
                  periodForm
                    .period_end,

                payment_date:
                  periodForm
                    .payment_date ||
                  null,
              }
            )

        setPeriodDialogOpen(
          false
        )

        setPeriodForm(
          initialPeriodForm()
        )

        showMessage(
          result?.message ||
            "Période créée avec succès."
        )

        await loadFormData()

        if (
          result?.item?.id
        ) {
          setSelectedPeriod(
            String(
              result.item.id
            )
          )
        }
      } catch (err) {
        showMessage(
          extractError(
            err,
            "Impossible de créer la période."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DELETE PERIOD
  // =======================================================

  const deletePeriod =
    async (
      period
    ) => {
      const payrollCount =
        Number(
          period?.payroll_count ||
            0
        )

      if (
        payrollCount > 0
      ) {
        showMessage(
          "Cette période contient déjà des bulletins et ne peut pas être supprimée.",
          "warning"
        )

        return
      }

      const accepted =
        window.confirm(
          `Supprimer la période "${period.name}" ?`
        )

      if (!accepted) {
        return
      }

      setSaving(true)

      try {
        const result =
          await hrPayrollService
            .deleteHrPayrollPeriod(
              period.id
            )

        showMessage(
          result?.message ||
            "Période supprimée."
        )

        if (
          String(
            selectedPeriod
          ) ===
          String(
            period.id
          )
        ) {
          setSelectedPeriod(
            "all"
          )
        }

        await refreshAll()
      } catch (err) {
        showMessage(
          extractError(
            err,
            "Impossible de supprimer la période."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // OPEN PAYROLL DIALOG
  // =======================================================

  const openPayrollDialog =
    () => {
      const periodId =
        selectedPeriod !==
        "all"
          ? selectedPeriod
          : periods?.[0]?.id ||
            ""

      setPayrollForm({
        ...initialPayrollForm(),

        payroll_period_id:
          periodId,
      })

      setPayrollPreview(
        null
      )

      setPayrollDialogOpen(
        true
      )
    }


  // =======================================================
  // PREVIEW
  // =======================================================

  const loadPreview =
    async (
      employeeId,
      periodId
    ) => {
      if (
        !employeeId ||
        !periodId
      ) {
        setPayrollPreview(
          null
        )

        return
      }

      try {
        let result = null

        if (
          typeof hrPayrollService
            .getHrPayrollPreview
          === "function"
        ) {
          result =
            await hrPayrollService
              .getHrPayrollPreview(
                {
                  employee_id:
                    Number(
                      employeeId
                    ),

                  payroll_period_id:
                    Number(
                      periodId
                    ),
                }
              )
        }

        if (!result) {
          const employee =
            employees.find(
              (item) =>
                String(
                  item.id
                )
                ===
                String(
                  employeeId
                )
            )

          result = {
            suggested: {
              base_salary:
                Number(
                  employee?.base_salary ||
                    0
                ),

              currency:
                employee?.currency ||
                "USD",

              overtime_amount:
                0,
            },

            attendance: {
              overtime_minutes:
                0,

              overtime_hours:
                0,

              hourly_rate:
                0,

              overtime_multiplier:
                1.5,

              overtime_amount:
                0,
            },
          }
        }

        setPayrollPreview(
          result
        )

        setPayrollForm(
          (current) => ({
            ...current,

            base_salary:
              Number(
                result?.suggested
                  ?.base_salary ||
                  0
              ),

            currency:
              result?.suggested
                ?.currency ||
              current.currency ||
              "USD",

            overtime_amount:
              Number(
                result?.suggested
                  ?.overtime_amount ||
                  0
              ),

            use_automatic_overtime:
              true,
          })
        )
      } catch (err) {
        console.error(
          "PAYROLL PREVIEW ERROR:",
          err
        )

        const employee =
          employees.find(
            (item) =>
              String(
                item.id
              )
              ===
              String(
                employeeId
              )
          )

        setPayrollPreview(
          null
        )

        setPayrollForm(
          (current) => ({
            ...current,

            base_salary:
              Number(
                employee?.base_salary ||
                  0
              ),

            currency:
              employee?.currency ||
              current.currency ||
              "USD",

            overtime_amount:
              0,

            use_automatic_overtime:
              true,
          })
        )
      }
    }


  // =======================================================
  // EMPLOYEE CHANGE
  // =======================================================

  const handleEmployeeChange =
    (
      event
    ) => {
      const employeeId =
        event.target.value

      setPayrollForm(
        (current) => ({
          ...current,

          employee_id:
            employeeId,
        })
      )

      loadPreview(
        employeeId,
        payrollForm
          .payroll_period_id
      )
    }


  // =======================================================
  // PERIOD CHANGE
  // =======================================================

  const handlePeriodChange =
    (
      event
    ) => {
      const periodId =
        event.target.value

      setPayrollForm(
        (current) => ({
          ...current,

          payroll_period_id:
            periodId,
        })
      )

      loadPreview(
        payrollForm
          .employee_id,
        periodId
      )
    }


  // =======================================================
  // CREATE PAYROLL
  // =======================================================

  const createPayroll =
    async () => {
      if (
        !payrollForm
          .payroll_period_id ||
        !payrollForm
          .employee_id
      ) {
        showMessage(
          "Sélectionnez une période et un employé.",
          "warning"
        )

        return
      }

      setSaving(true)

      try {
        const payload = {
          payroll_period_id:
            Number(
              payrollForm
                .payroll_period_id
            ),

          employee_id:
            Number(
              payrollForm
                .employee_id
            ),

          base_salary:
            Number(
              payrollForm
                .base_salary ||
                0
            ),

          bonuses:
            Number(
              payrollForm
                .bonuses ||
                0
            ),

          commissions:
            Number(
              payrollForm
                .commissions ||
                0
            ),

          overtime_amount:
            Number(
              payrollForm
                .overtime_amount ||
                0
            ),

          use_automatic_overtime:
            true,

          allowances:
            Number(
              payrollForm
                .allowances ||
                0
            ),

          deductions:
            Number(
              payrollForm
                .deductions ||
                0
            ),

          advances:
            Number(
              payrollForm
                .advances ||
                0
            ),

          currency:
            payrollForm
              .currency ||
            "USD",

          notes:
            payrollForm
              .notes
              ?.trim() ||
            null,
        }

        const result =
          await hrPayrollService
            .createHrPayroll(
              payload
            )

        setPayrollDialogOpen(
          false
        )

        setPayrollPreview(
          null
        )

        setPayrollForm(
          initialPayrollForm()
        )

        showMessage(
          result?.message ||
            "Bulletin créé avec succès."
        )

        await refreshAll()
      } catch (err) {
        console.error(
          "CREATE PAYROLL ERROR:",
          err?.response?.data ||
            err
        )

        showMessage(
          extractError(
            err,
            "Impossible de créer le bulletin de paie."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // VALIDATE PAYROLL
  // =======================================================

  const validatePayroll =
    async (
      id
    ) => {
      try {
        const result =
          await hrPayrollService
            .validateHrPayroll(
              id
            )

        showMessage(
          result?.message ||
            "Bulletin validé."
        )

        await loadData()
      } catch (err) {
        showMessage(
          extractError(
            err,
            "Impossible de valider le bulletin."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // PAY
  // =======================================================

  const payPayroll =
    async (
      id
    ) => {
      const accepted =
        window.confirm(
          "Confirmer le paiement de ce bulletin ?"
        )

      if (!accepted) {
        return
      }

      try {
        const result =
          await hrPayrollService
            .markHrPayrollPaid(
              id
            )

        showMessage(
          result?.message ||
            "Paiement enregistré."
        )

        await loadData()
      } catch (err) {
        showMessage(
          extractError(
            err,
            "Impossible d'enregistrer le paiement."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // DELETE PAYROLL
  // =======================================================

  const deletePayroll =
    async (
      row
    ) => {
      if (
        String(
          row?.status ||
            ""
        ).toUpperCase()
        === "PAID"
      ) {
        showMessage(
          "Un bulletin payé ne peut pas être supprimé.",
          "warning"
        )

        return
      }

      const accepted =
        window.confirm(
          `Supprimer le bulletin de ${row.employee_name || "cet employé"} ?`
        )

      if (!accepted) {
        return
      }

      try {
        const result =
          await hrPayrollService
            .deleteHrPayroll(
              row.id
            )

        showMessage(
          result?.message ||
            "Bulletin supprimé."
        )

        await refreshAll()
      } catch (err) {
        showMessage(
          extractError(
            err,
            "Impossible de supprimer le bulletin."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // DOWNLOAD PAYROLL PDF
  // =======================================================

  const downloadPayrollPdf =
    async (
      row
    ) => {
      if (
        !row?.id
      ) {
        showMessage(
          "Bulletin invalide.",
          "warning"
        )

        return
      }

      setDownloadingPdfId(
        row.id
      )

      try {
        const result =
          await hrPayrollService
            .downloadHrPayrollPdf(
              row.id
            )

        showMessage(
          `PDF téléchargé : ${
            result?.filename ||
            "bulletin de paie"
          }`
        )
      } catch (err) {
        console.error(
          "PAYROLL PDF ERROR:",
          err
        )

        showMessage(
          extractError(
            err,
            "Impossible de télécharger le bulletin PDF."
          ),
          "error"
        )
      } finally {
        setDownloadingPdfId(
          null
        )
      }
    }


  // =======================================================
  // DESKTOP COLUMNS
  // =======================================================

  const columns = [
    {
      field:
        "employee_name",

      headerName:
        "Employé",

      minWidth:
        190,

      flex:
        1.3,

      renderCell:
        (params) => (
          <Box
            sx={{
              py: 0.65,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={800}
              noWrap
            >
              {params.row
                .employee_name ||
                "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              {params.row
                .employee_code ||
                ""}
            </Typography>
          </Box>
        ),
    },

    {
      field:
        "period_name",

      headerName:
        "Période",

      minWidth:
        140,

      flex:
        0.8,

      renderCell:
        (params) =>
          params.row
            .period_name ||
          params.row
            .period_code ||
          "-",
    },

    {
      field:
        "base_salary",

      headerName:
        "Salaire base",

      minWidth:
        135,

      align:
        "right",

      headerAlign:
        "right",

      renderCell:
        (params) =>
          money(
            params.value,
            params.row
              .currency
          ),
    },

    {
      field:
        "additions",

      headerName:
        "Éléments +",

      minWidth:
        135,

      align:
        "right",

      headerAlign:
        "right",

      valueGetter:
        (_value, row) =>
          Number(
            row.bonuses ||
              0
          )
          +
          Number(
            row.commissions ||
              0
          )
          +
          Number(
            row.overtime_amount ||
              0
          )
          +
          Number(
            row.allowances ||
              0
          ),

      renderCell:
        (params) =>
          money(
            params.value,
            params.row
              .currency
          ),
    },

    {
      field:
        "reductions",

      headerName:
        "Retenues",

      minWidth:
        130,

      align:
        "right",

      headerAlign:
        "right",

      valueGetter:
        (_value, row) =>
          Number(
            row.deductions ||
              0
          )
          +
          Number(
            row.advances ||
              0
          ),

      renderCell:
        (params) =>
          money(
            params.value,
            params.row
              .currency
          ),
    },

    {
      field:
        "gross_salary",

      headerName:
        "Brut",

      minWidth:
        140,

      align:
        "right",

      headerAlign:
        "right",

      renderCell:
        (params) =>
          money(
            params.value,
            params.row
              .currency
          ),
    },

    {
      field:
        "net_salary",

      headerName:
        "Net à payer",

      minWidth:
        150,

      align:
        "right",

      headerAlign:
        "right",

      renderCell:
        (params) => (
          <Typography
            variant="body2"
            fontWeight={900}
          >
            {money(
              params.row
                .net_salary ??
                params.row
                  .net_amount,

              params.row
                .currency
            )}
          </Typography>
        ),
    },

    {
      field:
        "status",

      headerName:
        "Statut",

      minWidth:
        115,

      renderCell:
        (params) => {
          const config =
            resolveStatus(
              params.value
            )

          return (
            <Chip
              size="small"
              label={
                config.label
              }
              color={
                config.color
              }
            />
          )
        },
    },

    {
      field:
        "actions",

      headerName:
        "Actions",

      sortable:
        false,

      filterable:
        false,

      minWidth:
        285,

      renderCell:
        (params) => {
          const status =
            String(
              params.row
                .status ||
                ""
            ).toUpperCase()

          return (
            <Stack
              direction="row"
              spacing={0.7}
              alignItems="center"
            >
              {status ===
                "DRAFT" && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    validatePayroll(
                      params.row.id
                    )
                  }
                >
                  Valider
                </Button>
              )}

              {status ===
                "VALIDATED" && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() =>
                    payPayroll(
                      params.row.id
                    )
                  }
                >
                  Payer
                </Button>
              )}

              {status !==
                "PAID" && (
                <Button
                  size="small"
                  color="error"
                  onClick={() =>
                    deletePayroll(
                      params.row
                    )
                  }
                >
                  Supprimer
                </Button>
              )}

              <Button
                size="small"
                variant="outlined"
                startIcon={
                  <DownloadOutlinedIcon />
                }
                disabled={
                  downloadingPdfId ===
                  params.row.id
                }
                onClick={() =>
                  downloadPayrollPdf(
                    params.row
                  )
                }
              >
                {downloadingPdfId ===
                params.row.id
                  ? "PDF..."
                  : "PDF"}
              </Button>

              {status ===
                "PAID" && (
                <Typography
                  variant="caption"
                  color="success.main"
                  fontWeight={800}
                >
                  Terminé
                </Typography>
              )}
            </Stack>
          )
        },
    },
  ]


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          p: {
            xs: 1.25,
            sm: 2,
            md: 3,
          },

          maxWidth:
            "1600px",

          mx:
            "auto",
        }}
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <Stack
          direction={{
            xs: "column",
            lg: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            lg: "center",
          }}
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                fontSize: {
                  xs: "1.65rem",
                  md: "2.125rem",
                },
              }}
            >
              Paie
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Préparation, validation et suivi des bulletins
              de salaire du personnel.
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{
              "& .MuiButton-root":
                {
                  minHeight: 42,
                },
            }}
          >
            <Button
              variant="outlined"
              startIcon={
                <RefreshIcon />
              }
              onClick={
                refreshAll
              }
              disabled={
                loading
              }
            >
              Actualiser
            </Button>

            <Button
              variant="outlined"
              onClick={() =>
                setPeriodsManagerOpen(
                  true
                )
              }
            >
              Périodes
            </Button>

            <Button
              variant="outlined"
              startIcon={
                <AddIcon />
              }
              onClick={() => {
                setPeriodForm(
                  initialPeriodForm()
                )

                setPeriodDialogOpen(
                  true
                )
              }}
            >
              Nouvelle période
            </Button>

            <Button
              variant="contained"
              startIcon={
                <AddIcon />
              }
              onClick={
                openPayrollDialog
              }
              disabled={
                !periods.length
              }
            >
              Nouveau bulletin
            </Button>
          </Stack>
        </Stack>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"
          sx={{
            mb: 2,
          }}
        >
          Le brut et le net sont calculés automatiquement.
          Les heures supplémentaires enregistrées dans
          Présences sont récupérées automatiquement pour la
          période sélectionnée et intégrées au bulletin.
        </Alert>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
            }}
          >
            {error}
          </Alert>
        )}


        {/* =================================================
            KPIS
        ================================================= */}

        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns: {
              xs:
                "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              lg:
                "repeat(4, minmax(0, 1fr))",

              xl:
                "repeat(5, minmax(0, 1fr))",
            },

            gap:
              1.5,

            mb:
              2,
          }}
        >
          <KpiCard
            title="Bulletins"
            value={
              totals.count
            }
            subtitle="Bulletins enregistrés"
            icon={
              <AccountBalanceWalletOutlinedIcon />
            }
          />

          <KpiCard
            title="Brut total"
            value={
              money(
                totals.gross
              )
            }
            subtitle="Avant retenues"
            icon={
              <AccountBalanceWalletOutlinedIcon />
            }
          />

          <KpiCard
            title="Retenues & avances"
            value={
              money(
                totals.deductions
              )
            }
            subtitle="À déduire"
            icon={
              <PaymentsOutlinedIcon />
            }
          />

          <KpiCard
            title="Net à payer"
            value={
              money(
                totals.net
              )
            }
            subtitle="Masse salariale nette"
            icon={
              <PaymentsOutlinedIcon />
            }
          />

          <KpiCard
            title="Payés"
            value={
              totals.paid
            }
            subtitle="Bulletins réglés"
            icon={
              <PaymentsOutlinedIcon />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 1.25,
              sm: 1.5,
            },

            mb: 2,

            borderRadius: 3,

            border:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Box
            sx={{
              display:
                "grid",

              gridTemplateColumns: {
                xs:
                  "1fr",

                md:
                  "minmax(260px, 1.5fr) minmax(220px, 1fr) minmax(190px, .8fr)",
              },

              gap:
                1.25,
            }}
          >
            <TextField
              size="small"
              label="Rechercher"
              placeholder="Employé, matricule, période..."
              value={
                search
              }
              onChange={
                (event) =>
                  setSearch(
                    event.target
                      .value
                  )
              }
              fullWidth
            />

            <FormControl
              size="small"
              fullWidth
            >
              <InputLabel>
                Période
              </InputLabel>

              <Select
                value={
                  selectedPeriod
                }
                label="Période"
                onChange={
                  (event) =>
                    setSelectedPeriod(
                      event.target
                        .value
                    )
                }
              >
                <MenuItem
                  value="all"
                >
                  Toutes les périodes
                </MenuItem>

                {periods.map(
                  (period) => (
                    <MenuItem
                      key={
                        period.id
                      }
                      value={
                        String(
                          period.id
                        )
                      }
                    >
                      {period.code}
                      {" — "}
                      {period.name}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              fullWidth
            >
              <InputLabel>
                Statut
              </InputLabel>

              <Select
                value={
                  statusFilter
                }
                label="Statut"
                onChange={
                  (event) =>
                    setStatusFilter(
                      event.target
                        .value
                    )
                }
              >
                <MenuItem
                  value="all"
                >
                  Tous les statuts
                </MenuItem>

                <MenuItem
                  value="DRAFT"
                >
                  Brouillon
                </MenuItem>

                <MenuItem
                  value="VALIDATED"
                >
                  Validé
                </MenuItem>

                <MenuItem
                  value="PAID"
                >
                  Payé
                </MenuItem>

                <MenuItem
                  value="CANCELLED"
                >
                  Annulé
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>


        {/* =================================================
            REGISTER
        ================================================= */}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,

            border:
              "1px solid",

            borderColor:
              "divider",

            overflow:
              "hidden",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.5,
                sm: 2,
              },

              py: 1.7,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={800}
            >
              Registre de paie
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Suivi des bulletins, calculs, validations et
              paiements du personnel.
            </Typography>
          </Box>

          {mobile ? (
            <Box
              sx={{
                p: 1.25,
              }}
            >
              {loading ? (
                <Typography
                  color="text.secondary"
                  textAlign="center"
                  sx={{
                    py: 5,
                  }}
                >
                  Chargement...
                </Typography>
              ) : rows.length ===
                0 ? (
                <Typography
                  color="text.secondary"
                  textAlign="center"
                  sx={{
                    py: 5,
                  }}
                >
                  Aucun bulletin de paie.
                </Typography>
              ) : (
                <Stack
                  spacing={1.25}
                >
                  {rows.map(
                    (row) => (
                      <MobilePayrollCard
                        key={
                          row.id
                        }
                        row={
                          row
                        }
                        onValidate={
                          validatePayroll
                        }
                        onPay={
                          payPayroll
                        }
                        onDelete={
                          deletePayroll
                        }
                        onDownloadPdf={
                          downloadPayrollPdf
                        }
                      />
                    )
                  )}
                </Stack>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                width:
                  "100%",

                height:
                  555,
              }}
            >
              <DataGrid
                rows={
                  rows
                }
                columns={
                  columns
                }
                loading={
                  loading
                }
                disableRowSelectionOnClick
                pageSizeOptions={[
                  5,
                  10,
                  25,
                ]}
                initialState={{
                  pagination: {
                    paginationModel: {
                      page:
                        0,
                      pageSize:
                        10,
                    },
                  },
                }}
                rowHeight={
                  58
                }
                sx={{
                  border:
                    0,

                  "& .MuiDataGrid-columnHeaders":
                    {
                      bgcolor:
                        "action.hover",
                    },

                  "& .MuiDataGrid-cell":
                    {
                      borderColor:
                        "divider",
                    },
                }}
              />
            </Box>
          )}
        </Paper>


        {/* =================================================
            PERIOD MANAGER
        ================================================= */}

        <Dialog
          open={
            periodsManagerOpen
          }
          onClose={() =>
            !saving &&
            setPeriodsManagerOpen(
              false
            )
          }
          fullWidth
          fullScreen={
            mobile
          }
          maxWidth="md"
        >
          <DialogTitle
            fontWeight={900}
          >
            Gestion des périodes de paie
          </DialogTitle>

          <DialogContent
            dividers
          >
            <Alert
              severity="info"
              sx={{
                mb: 2,
              }}
            >
              Une période peut être supprimée uniquement
              lorsqu'aucun bulletin n'y est encore rattaché.
            </Alert>

            {periods.length ===
            0 ? (
              <Box
                sx={{
                  py: 6,
                  textAlign:
                    "center",
                }}
              >
                <Typography
                  color="text.secondary"
                >
                  Aucune période de paie enregistrée.
                </Typography>
              </Box>
            ) : (
              <Stack
                spacing={1.25}
              >
                {periods.map(
                  (period) => {
                    const count =
                      Number(
                        period.payroll_count ||
                          0
                      )

                    const config =
                      resolveStatus(
                        period.status ||
                          "DRAFT"
                      )

                    return (
                      <Paper
                        key={
                          period.id
                        }
                        elevation={0}
                        sx={{
                          p: 1.6,

                          border:
                            "1px solid",

                          borderColor:
                            "divider",

                          borderRadius:
                            2.5,
                        }}
                      >
                        <Stack
                          direction={{
                            xs: "column",
                            sm: "row",
                          }}
                          justifyContent="space-between"
                          alignItems={{
                            xs: "stretch",
                            sm: "center",
                          }}
                          spacing={1.5}
                        >
                          <Box>
                            <Typography
                              fontWeight={900}
                            >
                              {period.name}
                            </Typography>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {period.code}
                              {" • "}
                              Du{" "}
                              {formatDate(
                                period.period_start
                              )}{" "}
                              au{" "}
                              {formatDate(
                                period.period_end
                              )}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {count} bulletin(s)
                            </Typography>
                          </Box>

                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Chip
                              size="small"
                              label={
                                config.label
                              }
                              color={
                                config.color
                              }
                            />

                            <Button
                              size="small"
                              color="error"
                              disabled={
                                count > 0 ||
                                saving
                              }
                              onClick={() =>
                                deletePeriod(
                                  period
                                )
                              }
                            >
                              Supprimer
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    )
                  }
                )}
              </Stack>
            )}
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() =>
                setPeriodsManagerOpen(
                  false
                )
              }
            >
              Fermer
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            NEW PERIOD
        ================================================= */}

        <Dialog
          open={
            periodDialogOpen
          }
          onClose={() =>
            !saving &&
            setPeriodDialogOpen(
              false
            )
          }
          fullWidth
          fullScreen={
            mobile
          }
          maxWidth="sm"
        >
          <DialogTitle
            fontWeight={900}
          >
            Nouvelle période de paie
          </DialogTitle>

          <DialogContent
            dividers
          >
            <Alert
              severity="info"
              sx={{
                mb: 2,
              }}
            >
              Le code de la période est généré
              automatiquement par le système.
            </Alert>

            <Stack
              spacing={2}
            >
              <TextField
                label="Nom de la période *"
                value={
                  periodForm.name
                }
                onChange={
                  (event) =>
                    setPeriodForm(
                      (current) => ({
                        ...current,

                        name:
                          event.target
                            .value,
                      })
                    )
                }
                fullWidth
              />

              <Box
                sx={{
                  display:
                    "grid",

                  gridTemplateColumns: {
                    xs:
                      "1fr",

                    sm:
                      "1fr 1fr",
                  },

                  gap:
                    2,
                }}
              >
                <TextField
                  type="date"
                  label="Début *"
                  value={
                    periodForm
                      .period_start
                  }
                  onChange={
                    (event) =>
                      setPeriodForm(
                        (current) => ({
                          ...current,

                          period_start:
                            event.target
                              .value,
                        })
                      )
                  }
                  slotProps={{
                    inputLabel: {
                      shrink:
                        true,
                    },
                  }}
                  fullWidth
                />

                <TextField
                  type="date"
                  label="Fin *"
                  value={
                    periodForm
                      .period_end
                  }
                  onChange={
                    (event) =>
                      setPeriodForm(
                        (current) => ({
                          ...current,

                          period_end:
                            event.target
                              .value,
                        })
                      )
                  }
                  slotProps={{
                    inputLabel: {
                      shrink:
                        true,
                    },
                  }}
                  fullWidth
                />
              </Box>

              <TextField
                type="date"
                label="Date prévue de paiement"
                value={
                  periodForm
                    .payment_date
                }
                onChange={
                  (event) =>
                    setPeriodForm(
                      (current) => ({
                        ...current,

                        payment_date:
                          event.target
                            .value,
                      })
                    )
                }
                slotProps={{
                  inputLabel: {
                    shrink:
                      true,
                  },
                }}
                fullWidth
              />
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() =>
                setPeriodDialogOpen(
                  false
                )
              }
              disabled={
                saving
              }
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={
                createPeriod
              }
              disabled={
                saving
              }
            >
              {saving
                ? "Création..."
                : "Créer la période"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            NEW PAYROLL
        ================================================= */}

        <Dialog
          open={
            payrollDialogOpen
          }
          onClose={() =>
            !saving &&
            setPayrollDialogOpen(
              false
            )
          }
          fullWidth
          fullScreen={
            mobile
          }
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: {
                xs: 0,
                md: 3,
              },
            },
          }}
        >
          <DialogTitle>
            <Typography
              variant="h6"
              fontWeight={900}
            >
              Préparer un bulletin de paie
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Complétez les éléments variables du salaire.
              Le brut et le net sont calculés en temps réel.
            </Typography>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              bgcolor:
                "background.default",

              p: {
                xs: 1.5,
                sm: 2,
              },
            }}
          >
            <Stack
              spacing={2}
            >
              {/* ===========================================
                  CONTEXTE
              =========================================== */}

              <FormSection
                title="1. Employé et période"
                subtitle="Sélectionnez la personne concernée et la période de paie."
              >
                <Box
                  sx={{
                    display:
                      "grid",

                    gridTemplateColumns: {
                      xs:
                        "1fr",

                      md:
                        "1fr 1fr",
                    },

                    gap:
                      2,
                  }}
                >
                  <FormControl
                    fullWidth
                  >
                    <InputLabel>
                      Période *
                    </InputLabel>

                    <Select
                      value={
                        payrollForm
                          .payroll_period_id
                      }
                      label="Période *"
                      onChange={
                        handlePeriodChange
                      }
                    >
                      {periods.map(
                        (period) => (
                          <MenuItem
                            key={
                              period.id
                            }
                            value={
                              period.id
                            }
                          >
                            {period.code}
                            {" — "}
                            {period.name}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>

                  <FormControl
                    fullWidth
                  >
                    <InputLabel>
                      Employé *
                    </InputLabel>

                    <Select
                      value={
                        payrollForm
                          .employee_id
                      }
                      label="Employé *"
                      onChange={
                        handleEmployeeChange
                      }
                    >
                      {employees.map(
                        (employee) => (
                          <MenuItem
                            key={
                              employee.id
                            }
                            value={
                              employee.id
                            }
                          >
                            {employee.employee_code}
                            {" — "}
                            {employee.employee_name}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </FormSection>


              {/* ===========================================
                  SALAIRE FIXE
              =========================================== */}

              <FormSection
                title="2. Salaire de référence"
                subtitle="Le salaire de base provient automatiquement de la fiche employé."
              >
                <Box
                  sx={{
                    display:
                      "grid",

                    gridTemplateColumns: {
                      xs:
                        "1fr",

                      sm:
                        "2fr 1fr",
                    },

                    gap:
                      2,
                  }}
                >
                  <NumberField
                    label="Salaire de base"
                    value={
                      payrollForm
                        .base_salary
                    }
                    onChange={
                      (event) =>
                        setPayrollForm(
                          (current) => ({
                            ...current,

                            base_salary:
                              event.target
                                .value,
                          })
                        )
                    }
                  />

                  <FormControl
                    fullWidth
                  >
                    <InputLabel>
                      Devise
                    </InputLabel>

                    <Select
                      value={
                        payrollForm
                          .currency
                      }
                      label="Devise"
                      onChange={
                        (event) =>
                          setPayrollForm(
                            (current) => ({
                              ...current,

                              currency:
                                event.target
                                  .value,
                            })
                          )
                      }
                    >
                      <MenuItem
                        value="USD"
                      >
                        USD
                      </MenuItem>

                      <MenuItem
                        value="CDF"
                      >
                        CDF
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </FormSection>


              {/* ===========================================
                  ADDITIONS
              =========================================== */}

              <FormSection
                title="3. Éléments à ajouter"
                subtitle="Primes, commissions, indemnités et heures supplémentaires."
              >
                <Box
                  sx={{
                    display:
                      "grid",

                    gridTemplateColumns: {
                      xs:
                        "1fr",

                      sm:
                        "1fr 1fr",
                    },

                    gap:
                      2,
                  }}
                >
                  <NumberField
                    label="Primes"
                    value={
                      payrollForm
                        .bonuses
                    }
                    onChange={
                      (event) =>
                        setPayrollForm(
                          (current) => ({
                            ...current,

                            bonuses:
                              event.target
                                .value,
                          })
                        )
                    }
                  />

                  <NumberField
                    label="Commissions"
                    value={
                      payrollForm
                        .commissions
                    }
                    onChange={
                      (event) =>
                        setPayrollForm(
                          (current) => ({
                            ...current,

                            commissions:
                              event.target
                                .value,
                          })
                        )
                    }
                  />

                  <NumberField
                    label="Indemnités"
                    value={
                      payrollForm
                        .allowances
                    }
                    onChange={
                      (event) =>
                        setPayrollForm(
                          (current) => ({
                            ...current,

                            allowances:
                              event.target
                                .value,
                          })
                        )
                    }
                  />

                  <NumberField
                    label="Montant heures supplémentaires"
                    value={
                      payrollForm
                        .overtime_amount
                    }
                    onChange={() => {}}
                    disabled
                    helperText={
                      payrollPreview
                        ? `${Number(
                            payrollPreview
                              ?.attendance
                              ?.overtime_hours ||
                              0
                          )} h • taux horaire ${money(
                            payrollPreview
                              ?.attendance
                              ?.hourly_rate ||
                              0,
                            payrollForm
                              .currency
                          )} • coefficient ×${Number(
                            payrollPreview
                              ?.attendance
                              ?.overtime_multiplier ||
                              0
                          )}`
                        : "Sélectionnez une période et un employé pour calculer automatiquement les heures supplémentaires."
                    }
                  />
                </Box>

                {payrollPreview && (
                  <Alert
                    severity="info"
                    sx={{
                      mt: 2,
                    }}
                  >
                    Présences a retourné{" "}
                    <strong>
                      {Number(
                        payrollPreview
                          ?.attendance
                          ?.overtime_hours ||
                          0
                      )} h
                    </strong>{" "}
                    d'heures supplémentaires pour cette période,
                    soit{" "}
                    <strong>
                      {money(
                        payrollPreview
                          ?.suggested
                          ?.overtime_amount ||
                          0,
                        payrollForm
                          .currency
                      )}
                    </strong>{" "}
                    calculé automatiquement.
                  </Alert>
                )}
              </FormSection>


              {/* ===========================================
                  DEDUCTIONS
              =========================================== */}

              <FormSection
                title="4. Éléments à déduire"
                subtitle="Retenues et avances à récupérer sur le salaire."
              >
                <Box
                  sx={{
                    display:
                      "grid",

                    gridTemplateColumns: {
                      xs:
                        "1fr",

                      sm:
                        "1fr 1fr",
                    },

                    gap:
                      2,
                  }}
                >
                  <NumberField
                    label="Retenues"
                    value={
                      payrollForm
                        .deductions
                    }
                    onChange={
                      (event) =>
                        setPayrollForm(
                          (current) => ({
                            ...current,

                            deductions:
                              event.target
                                .value,
                          })
                        )
                    }
                  />

                  <NumberField
                    label="Avances"
                    value={
                      payrollForm
                        .advances
                    }
                    onChange={
                      (event) =>
                        setPayrollForm(
                          (current) => ({
                            ...current,

                            advances:
                              event.target
                                .value,
                          })
                        )
                    }
                  />
                </Box>
              </FormSection>


              {/* ===========================================
                  RESULT
              =========================================== */}

              <Paper
                elevation={0}
                sx={{
                  p: {
                    xs: 1.5,
                    sm: 2,
                  },

                  borderRadius: 3,

                  border:
                    "1px solid",

                  borderColor:
                    "divider",

                  bgcolor:
                    "background.paper",
                }}
              >
                <Typography
                  fontWeight={900}
                  sx={{
                    mb: 1.5,
                  }}
                >
                  Résumé du bulletin
                </Typography>

                <Box
                  sx={{
                    display:
                      "grid",

                    gridTemplateColumns: {
                      xs:
                        "1fr",

                      sm:
                        "repeat(3, 1fr)",
                    },

                    gap:
                      1.5,
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,

                      bgcolor:
                        "action.hover",

                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Salaire brut
                    </Typography>

                    <Typography
                      variant="h6"
                      fontWeight={900}
                    >
                      {money(
                        formCalculation
                          .gross,
                        payrollForm
                          .currency
                      )}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,

                      bgcolor:
                        "action.hover",

                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Total des retenues
                    </Typography>

                    <Typography
                      variant="h6"
                      fontWeight={900}
                    >
                      {money(
                        formCalculation
                          .reductions,
                        payrollForm
                          .currency
                      )}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,

                      border:
                        "1px solid",

                      borderColor:
                        "success.main",

                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Net à payer
                    </Typography>

                    <Typography
                      variant="h5"
                      color="success.main"
                      fontWeight={900}
                    >
                      {money(
                        formCalculation
                          .net,
                        payrollForm
                          .currency
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Paper>


              {/* ===========================================
                  NOTES
              =========================================== */}

              <TextField
                label="Notes / observations"
                value={
                  payrollForm.notes
                }
                onChange={
                  (event) =>
                    setPayrollForm(
                      (current) => ({
                        ...current,

                        notes:
                          event.target
                            .value,
                      })
                    )
                }
                multiline
                minRows={3}
                fullWidth
              />
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              px: 2,
              py: 1.5,

              gap: 1,

              flexDirection: {
                xs: "column-reverse",
                sm: "row",
              },

              "& .MuiButton-root":
                {
                  width: {
                    xs: "100%",
                    sm: "auto",
                  },
                },
            }}
          >
            <Button
              onClick={() =>
                setPayrollDialogOpen(
                  false
                )
              }
              disabled={
                saving
              }
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={
                createPayroll
              }
              disabled={
                saving
              }
              sx={{
                minWidth: 180,
              }}
            >
              {saving
                ? "Création..."
                : "Créer le bulletin"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            SNACKBAR
        ================================================= */}

        <Snackbar
          open={
            snackbar.open
          }
          autoHideDuration={
            5000
          }
          anchorOrigin={{
            vertical:
              "top",
            horizontal:
              "right",
          }}
          onClose={() =>
            setSnackbar(
              (current) => ({
                ...current,
                open:
                  false,
              })
            )
          }
        >
          <Alert
            variant="filled"
            severity={
              snackbar.severity
            }
            onClose={() =>
              setSnackbar(
                (current) => ({
                  ...current,
                  open:
                    false,
                })
              )
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  )
}